/**
 * Turns the four rules YAML sources into a validated `RuleSet`: zod parses
 * each file's shape (schema.ts), then cross-checks reject rules the sim
 * can't run (unknown ids, impossible unlocks, ranges past vision, upgrade
 * loops...). A rules mistake fails at load, never mid-match.
 */
import { parse } from 'yaml';
import {
  BuildingDefMapSchema,
  GameRulesSchema,
  MAX_SHOT_TICKS,
  ResourceDefMapSchema,
  type RuleSet,
  UnitDefMapSchema,
} from './schema';

interface RuleSetSources {
  resources: string;
  units: string;
  buildings: string;
  rules: string;
}

function assertKnownIds(
  ids: Iterable<string>,
  known: Record<string, unknown>,
  context: string
): void {
  const unknown = [...ids].filter((id) => !(id in known));
  if (unknown.length > 0) {
    throw new Error(`${context} references unknown ids: ${unknown.join(', ')}`);
  }
}

function loadRuleSet(sources: RuleSetSources): RuleSet {
  const resources = ResourceDefMapSchema.parse(parse(sources.resources));
  const units = UnitDefMapSchema.parse(parse(sources.units));
  const buildings = BuildingDefMapSchema.parse(parse(sources.buildings));
  const { pacing, shots, veterancy, match, player } = GameRulesSchema.parse(parse(sources.rules));

  // Era gates (`cityLevel`) name a level of the city holding the building, so
  // they can't ask for more than the highest level a city reaches.
  const cityMaxLevel = Math.max(
    0,
    ...Object.values(buildings)
      .filter((b) => b.territoryRadius !== undefined)
      .map((b) => 1 + (b.levels?.length ?? 0))
  );
  const assertCityLevel = (level: number | undefined, context: string) => {
    if (level !== undefined && level > cityMaxLevel) {
      throw new Error(`${context} ${level}, but cities top out at level ${cityMaxLevel}`);
    }
  };

  for (const [id, unit] of Object.entries(units)) {
    assertKnownIds(Object.keys(unit.cost), resources, `units.yaml: ${id}.cost`);
    assertKnownIds(Object.keys(unit.upkeep ?? {}), resources, `units.yaml: ${id}.upkeep`);
    assertKnownIds(Object.keys(unit.bonusVs ?? {}), units, `units.yaml: ${id}.bonusVs`);
  }
  for (const [id, building] of Object.entries(buildings)) {
    assertKnownIds([building.builtBy], units, `buildings.yaml: ${id}.builtBy`);
    assertKnownIds(Object.keys(building.cost ?? {}), resources, `buildings.yaml: ${id}.cost`);
    assertKnownIds(building.spawns ?? [], units, `buildings.yaml: ${id}.spawns`);
    assertKnownIds(building.accepts ?? [], units, `buildings.yaml: ${id}.accepts`);
    if (building.stores) {
      assertKnownIds(building.stores, units, `buildings.yaml: ${id}.stores`);
      if (!building.capacity) {
        throw new Error(`buildings.yaml: ${id}.stores needs 'capacity'`);
      }
      const civilians = building.stores.filter((type) => !units[type]?.military);
      if (civilians.length > 0) {
        throw new Error(`buildings.yaml: ${id}.stores non-military units: ${civilians.join(', ')}`);
      }
    }
    // Units train where they'll be stored (sim/systems/training.ts).
    if (
      [building, ...(building.levels ?? [])].some((l) => l.trainSlots !== undefined) &&
      !building.capacity
    ) {
      throw new Error(`buildings.yaml: ${id}.trainSlots needs 'capacity'`);
    }
    assertCityLevel(building.placement?.cityLevel, `buildings.yaml: ${id}.placement.cityLevel is`);
    for (const [i, level] of (building.levels ?? []).entries()) {
      assertKnownIds(Object.keys(level.cost), resources, `buildings.yaml: ${id}.levels[${i}].cost`);
      // Only a placed building has a city holding it (a city's own levels have none).
      if (level.cityLevel !== undefined && !building.placement) {
        throw new Error(`buildings.yaml: ${id}.levels[${i}].cityLevel needs 'placement'`);
      }
      assertCityLevel(level.cityLevel, `buildings.yaml: ${id}.levels[${i}].cityLevel is`);
    }
    // As for units: a building firing past its sight (at least its territory,
    // see vision.ts) would hit and reveal what's in the fog. Checked at every
    // level, each stat carrying over from the level before.
    let range = 1;
    let vision = 0;
    let territory = 0;
    let strength = 0;
    let fires = false;
    for (const [i, level] of [building, ...(building.levels ?? [])].entries()) {
      range = level.range ?? range;
      vision = level.visionRadius ?? vision;
      territory = level.territoryRadius ?? territory;
      strength = level.strength ?? strength;
      if (strength > 0 && range > Math.max(vision, territory)) {
        throw new Error(`buildings.yaml: ${id} at level ${i + 1} fires further than it sees`);
      }
      fires ||= strength > 0;
    }
    if (!fires && [building, ...(building.levels ?? [])].some((level) => level.shot)) {
      throw new Error(`buildings.yaml: ${id}.shot on a building that never fires`);
    }
    // A watch spot needs both its reach and its sight, set on level 1 (levels
    // only change them); like cityHp, cityVision needs a city holding it.
    const levels = building.levels ?? [];
    if ((building.watchRange === undefined) !== (building.watchRadius === undefined)) {
      throw new Error(`buildings.yaml: ${id} needs watchRange and watchRadius together`);
    }
    if (
      building.watchRange === undefined &&
      levels.some((l) => l.watchRange !== undefined || l.watchRadius !== undefined)
    ) {
      throw new Error(`buildings.yaml: ${id}.levels set a watch on a building without one`);
    }
    if (
      (building.watchRange !== undefined ||
        [building, ...levels].some((l) => l.cityVision !== undefined)) &&
      !building.placement
    ) {
      throw new Error(`buildings.yaml: ${id}.watchRange / cityVision needs 'placement'`);
    }
    if (building.provides) {
      assertKnownIds([building.provides.resource], resources, `buildings.yaml: ${id}.provides`);
    }
    // Growth spreads over the land a placed building needs, adding worker
    // slots (providing buildings) or storage (buildings with capacity).
    if (building.growth && !(building.placement && (building.provides || building.capacity))) {
      throw new Error(
        `buildings.yaml: ${id}.growth needs 'placement' and 'provides' or 'capacity'`
      );
    }
    // Exactly one bonus per extra tile, matching what the building does.
    if (building.growth) {
      const { slotsPerExtraTile: slots, capacityPerExtraTile: capacity } = building.growth;
      if ((slots === undefined) === (capacity === undefined)) {
        throw new Error(
          `buildings.yaml: ${id}.growth needs exactly one of 'slotsPerExtraTile' or 'capacityPerExtraTile'`
        );
      }
      if (slots !== undefined && !building.provides) {
        throw new Error(`buildings.yaml: ${id}.growth.slotsPerExtraTile needs 'provides'`);
      }
      if (capacity !== undefined && !building.capacity) {
        throw new Error(`buildings.yaml: ${id}.growth.capacityPerExtraTile needs 'capacity'`);
      }
    }
  }
  // Contradictions the schema can't see on its own.
  const spawnable = new Set(Object.values(buildings).flatMap((b) => b.spawns ?? []));
  // Units only reached by promotion (a spy) are capped per completed city.
  const promotedTo = new Set(
    Object.values(units).flatMap((u) => (u.upgrades ?? []).map((up) => up.to))
  );
  for (const [id, unit] of Object.entries(units)) {
    if (unit.automate === 'work' && !unit.actions.includes('gather')) {
      throw new Error(`units.yaml: ${id}.automate is 'work' but it has no 'gather' action`);
    }
    const attacks = unit.actions.includes('attack') && unit.strength > 0;
    const heals = unit.actions.includes('heal');
    // A healer needs both what it heals and how fast; neither means anything without it.
    if (heals !== (unit.healRate !== undefined) || heals !== (unit.heals !== undefined)) {
      throw new Error(`units.yaml: ${id} needs the 'heal' action, healRate and heals together`);
    }
    // Army units fight or tend the fighters (healers ride with the army).
    if (unit.military && !(attacks || heals)) {
      throw new Error(
        `units.yaml: ${id}.military needs the 'attack' action and some strength, or 'heal'`
      );
    }
    if ((unit.bonusVs || unit.targets) && !attacks) {
      throw new Error(
        `units.yaml: ${id}.bonusVs/targets need the 'attack' action and some strength`
      );
    }
    if (unit.bonusVsBuildings !== undefined && !attacks) {
      throw new Error(
        `units.yaml: ${id}.bonusVsBuildings needs the 'attack' action and some strength`
      );
    }
    if (unit.shot && !(attacks && unit.range >= 2)) {
      throw new Error(
        `units.yaml: ${id}.shot needs the 'attack' action, some strength and range 2+`
      );
    }
    if (unit.bonusVsBuildings !== undefined && unit.targets === 'units') {
      throw new Error(`units.yaml: ${id}.bonusVsBuildings on a unit that never hits buildings`);
    }
    // Combat picks targets in range without checking vision: a range past
    // the unit's own sight would fire into (and so reveal) the fog.
    if (unit.range > unit.visionRadius) {
      throw new Error(`units.yaml: ${id}.range is more than its visionRadius`);
    }
    // A timed unit trains at the store that would hold it.
    if (unit.trainTime !== undefined && !unit.military) {
      throw new Error(`units.yaml: ${id}.trainTime needs 'military'`);
    }
    if (unit.costPerCity !== undefined && !unit.actions.includes('found-city')) {
      throw new Error(`units.yaml: ${id}.costPerCity needs the 'found-city' action`);
    }
    if (unit.perCity !== undefined && !spawnable.has(id) && !promotedTo.has(id)) {
      throw new Error(`units.yaml: ${id}.perCity is set but no building spawns it`);
    }
    if (unit.disguiseAs !== undefined) {
      assertKnownIds([unit.disguiseAs], units, `units.yaml: ${id}.disguiseAs`);
      if (!unit.stealth) throw new Error(`units.yaml: ${id}.disguiseAs needs stealth`);
    }
    // Stealth hides it from combat and threat checks, so it mustn't fight
    // (it would be an untouchable attacker) or join the army; and it holds
    // no tile, which occupancy only handles for a unit that never works (an
    // untouchable builder or founder) or goes inside a building.
    const works = unit.actions.some((a) => a === 'gather' || a === 'build' || a === 'found-city');
    const goesInside = Object.values(buildings).some((b) => b.accepts?.includes(id));
    if (unit.stealth && (attacks || heals || unit.military || works || goesInside)) {
      throw new Error(
        `units.yaml: ${id}.stealth on a unit that fights, heals, is military, works or goes inside buildings`
      );
    }
    if (unit.perCityLevel !== undefined && unit.perCity === undefined) {
      throw new Error(`units.yaml: ${id}.perCityLevel needs perCity`);
    }
    if (unit.requires) {
      const { building, level } = unit.requires;
      assertKnownIds([building], buildings, `units.yaml: ${id}.requires`);
      const max = 1 + (buildings[building]?.levels?.length ?? 0);
      if (level > max) {
        throw new Error(
          `units.yaml: ${id}.requires ${building} level ${level}, but it tops out at ${max}`
        );
      }
    }
    if (unit.gatherMultiplier !== undefined && !unit.actions.includes('gather')) {
      throw new Error(`units.yaml: ${id}.gatherMultiplier needs the 'gather' action`);
    }
    // One level of sharing: the unit counts as a capped type that counts as
    // itself, so a cap can't be dodged or chained.
    if (unit.capAs !== undefined) {
      assertKnownIds([unit.capAs], units, `units.yaml: ${id}.capAs`);
      const shared = units[unit.capAs];
      if (unit.capAs === id || shared?.capAs !== undefined || shared?.perCity === undefined) {
        throw new Error(`units.yaml: ${id}.capAs must name another unit with its own perCity`);
      }
      if (unit.perCity !== undefined) {
        throw new Error(`units.yaml: ${id} has both perCity and capAs`);
      }
    }
    for (const [i, upgrade] of (unit.upgrades ?? []).entries()) {
      const at = `units.yaml: ${id}.upgrades[${i}]`;
      assertKnownIds([upgrade.to], units, `${at}.to`);
      assertKnownIds(Object.keys(upgrade.cost), resources, `${at}.cost`);
      const to = units[upgrade.to];
      if (upgrade.to === id) throw new Error(`${at} upgrades ${id} into itself`);
      // A civilian may join the army (a worker into an engineer), but a
      // military unit never leaves it.
      if (unit.military && !to?.military) {
        throw new Error(`${at}: military ${id} can't become non-military ${upgrade.to}`);
      }
      if (upgrade.at) {
        const { building, level } = upgrade.at;
        assertKnownIds([building], buildings, `${at}.at`);
        const max = 1 + (buildings[building]?.levels?.length ?? 0);
        if (level > max) {
          throw new Error(`${at}.at ${building} level ${level}, but it tops out at ${max}`);
        }
      }
      // A promotion can't skip the new type's unlock: `at` must meet its
      // `requires` (the same building, at least that level).
      const req = to?.requires;
      if (req && !(upgrade.at?.building === req.building && upgrade.at.level >= req.level)) {
        throw new Error(
          `${at}.at must be at least ${req.building} level ${req.level}, which ${upgrade.to} requires`
        );
      }
    }
  }
  // Upgrades form no loops (a unit could otherwise cycle back through its types).
  const visiting = new Set<string>();
  const done = new Set<string>();
  const visit = (id: string): void => {
    if (done.has(id)) return;
    if (visiting.has(id)) throw new Error(`units.yaml: upgrades loop through ${id}`);
    visiting.add(id);
    for (const upgrade of units[id]?.upgrades ?? []) visit(upgrade.to);
    visiting.delete(id);
    done.add(id);
  };
  for (const id of Object.keys(units)) visit(id);
  // Shot timings go into state as ticks (100 ms each), which the wire
  // validator bounds: a reload or flight past MAX_SHOT_TICKS would make every
  // snapshot fail isSimState.
  const assertFireInterval = (fireInterval: number | undefined, context: string) => {
    if (fireInterval !== undefined && Math.round(fireInterval * 10) > MAX_SHOT_TICKS) {
      throw new Error(`${context}.fireInterval over ${MAX_SHOT_TICKS / 10} s`);
    }
  };
  assertFireInterval(shots.fireInterval, 'rules.yaml: shots');
  for (const [id, unit] of Object.entries(units)) {
    assertFireInterval(unit.shot?.fireInterval, `units.yaml: ${id}.shot`);
  }
  for (const [id, building] of Object.entries(buildings)) {
    for (const [i, level] of [building, ...(building.levels ?? [])].entries()) {
      const at = i === 0 ? '' : `.levels[${i - 1}]`;
      assertFireInterval(level.shot?.fireInterval, `buildings.yaml: ${id}${at}.shot`);
    }
  }
  const maxRange = Math.max(
    1,
    ...Object.values(units).map((u) => u.range),
    ...Object.values(buildings).flatMap((b) => [b, ...(b.levels ?? [])].map((l) => l.range ?? 1))
  );
  for (const [kind, { base, perHex }] of Object.entries(shots.flight)) {
    if (Math.round(base + perHex * maxRange) > MAX_SHOT_TICKS) {
      throw new Error(
        `rules.yaml: shots.flight.${kind} flies over ${MAX_SHOT_TICKS} ticks at range ${maxRange}`
      );
    }
  }
  assertKnownIds(Object.keys(veterancy.trainCost), resources, 'rules.yaml: veterancy.trainCost');
  if (veterancy.ranks.some((xp, i) => i > 0 && xp <= veterancy.ranks[i - 1])) {
    throw new Error('rules.yaml: veterancy.ranks must be ascending');
  }
  assertKnownIds(
    Object.keys(player.startingResources),
    resources,
    'rules.yaml: player.startingResources'
  );
  assertKnownIds(player.startingUnits, units, 'rules.yaml: player.startingUnits');
  assertKnownIds(player.startingBuildings, buildings, 'rules.yaml: player.startingBuildings');

  return { resources, units, buildings, pacing, shots, veterancy, match, player };
}

export type { RuleSetSources };
export { loadRuleSet };
